import React, {ComponentProps} from 'react'

import {useFormik} from 'formik'
import * as Yup from 'yup'

import {Filter, FilterParams, RangeMatchingFilter, getCount} from '../../libs/table-row-filter'

import {MinMaxRange} from '../../types'

import Button from '../../../../commons/src/components/button'
import FilterSelect from '../../../../commons/src/components/filter-select'
import FilterSelectFlyout from '../../../../commons/src/components/filter-select-flyout/filter-select-flyout'
import NumberInputField from '../../../../commons/src/components/number-input-field'

type FilterSelectProps = ComponentProps<typeof FilterSelect>

type Props<TableRow> = {
  label: string
  filterKey: string
  filterMap: Record<string, Filter<TableRow, FilterParams<TableRow>>>
  tableRows: TableRow[]
  applyFilter: (newFilter: RangeMatchingFilter<TableRow>) => void
  size?: FilterSelectProps['size']
}

type FormData = {
  min?: string
  max?: string
}

const initialValues = {
  min: '',
  max: '',
}

const validationSchema = {
  min: Yup.number().label('Minimum').integer().min(1),
  max: Yup.number()
    .label('Maximum')
    .integer()
    .min(1)
    .test('interval', 'Maximum has to be equal or larger than Minimum', (value, ctx) => {
      if (value && ctx.parent.min && ctx.parent.min > value) {
        return ctx.createError({path: ctx.path})
      }
      return true
    }),
}

const RangeMatchingFilterPartial = <TableRow,>({
  label,
  filterKey,
  filterMap,
  tableRows,
  applyFilter,
  size = '100',
}: Props<TableRow>) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const filter = filterMap[filterKey] as RangeMatchingFilter<TableRow>

  const formValuesToRange = (values: FormData): MinMaxRange => ({
    min: values.min ? parseInt(values.min, 10) : undefined,
    max: values.max ? parseInt(values.max, 10) : undefined,
  })

  const rangeToFormData = (range: MinMaxRange) => ({
    min: range.min ? range.min.toString() : '',
    max: range.max ? range.max.toString() : '',
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...rangeToFormData(filter.serialize().range),
    },
    validationSchema: Yup.object().shape(validationSchema),
    onSubmit: values => {
      const newRange = formValuesToRange(values)
      applyFilter(
        new RangeMatchingFilter({
          ...filter.serialize(),
          range: newRange,
        })
      )
      setIsOpen(false)
    },
  })

  React.useEffect(() => {
    if (isOpen) {
      formik.resetForm()
    }
  }, [isOpen])

  const getCountFor = (newRange: MinMaxRange) => {
    const newFilter = new RangeMatchingFilter({
      ...filter.serialize(),
      range: newRange,
    })
    return getCount(tableRows, filterMap, filterKey, newFilter)
  }

  const getButtonCount = (values: FormData) => {
    const range = formValuesToRange(values)
    return getCountFor(range)
  }

  const handleClear = () => {
    const newRange = formValuesToRange(initialValues)
    applyFilter(
      new RangeMatchingFilter({
        ...filter.serialize(),
        range: newRange,
      })
    )
    setIsOpen(false)
  }

  return (
    <FilterSelect
      label={filter.getLabel(label)}
      size={size}
      active={filter.isActive()}
      isOpen={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      onShouldClose={() => setIsOpen(false)}
      menu={
        <FilterSelectFlyout
          clearLabel="Clear"
          submitButton={
            <Button
              block
              disabled={formik.isSubmitting || Object.keys(formik.errors).length > 0}
              onClick={() => {
                formik.handleSubmit()
              }}
            >
              Apply ({getButtonCount(formik.values)})
            </Button>
          }
          onClear={handleClear}
        >
          <form>
            <NumberInputField
              className="u-margin-bottom"
              placeholder="min"
              name="min"
              value={formik.values.min}
              suffix="days"
              appearance={formik.errors.min ? 'error' : undefined}
              onChange={(v, n, event) => {
                formik.handleChange(event)
              }}
              onBlur={formik.handleBlur}
            />
            <NumberInputField
              placeholder="max"
              name="max"
              value={formik.values.max}
              suffix="days"
              appearance={formik.errors.max ? 'error' : undefined}
              onChange={(v, n, event) => {
                formik.handleChange(event)
              }}
              onBlur={formik.handleBlur}
            />
          </form>
        </FilterSelectFlyout>
      }
    />
  )
}

export default RangeMatchingFilterPartial
